import api from './api';
import {
  CampaignFilterParams,
  CampaignVariable,
  CampaignWriteRequest,
  CreateTemplateRequest,
  EmailAnalytics,
  EmailCampaign,
  EmailTemplate,
  MessageResponse,
  NewsletterStats,
  NewsletterSubscriber,
  Paginated,
  RecipientFilter,
  SubscriberFilterParams,
  TemplateFilterParams,
} from '../types/emailMarketing';
import { SubscribePayload } from '../utils/newsletter';

// --- Publikus (bejelentkezés nélkül) --------------------------------------------

export const subscribeToNewsletter = async (payload: SubscribePayload) => {
  const response = await api.post<MessageResponse>('/newsletter/subscribers/subscribe/', payload);
  return response.data;
};

export interface ConfirmSubscriptionResponse extends MessageResponse {
  already_confirmed: boolean;
  coupon_code: string | null;
  coupon_valid_until: string | null;
}

export const confirmNewsletterSubscription = async (token: string) => {
  const response = await api.post<ConfirmSubscriptionResponse>('/newsletter/subscribers/confirm/', { token });
  return response.data;
};

export const confirmUnsubscribe = async (token: string) => {
  const response = await api.post<MessageResponse & { already_unsubscribed: boolean }>(
    '/newsletter/subscribers/confirm-unsubscribe/',
    { token },
  );
  return response.data;
};

export const requestUnsubscribeLink = async (email: string) => {
  const response = await api.post<MessageResponse>('/newsletter/subscribers/unsubscribe/', { email });
  return response.data;
};

// --- Feliratkozók (admin) -----------------------------------------------------------

export const getSubscribers = async (params?: SubscriberFilterParams) => {
  const response = await api.get<Paginated<NewsletterSubscriber>>('/newsletter/subscribers/', { params });
  return response.data;
};

export const getSubscriberStats = async () => {
  const response = await api.get<NewsletterStats>('/newsletter/subscribers/stats/');
  return response.data;
};

export const createSubscriber = async (data: { email: string; first_name?: string; last_name?: string; tags?: string[] }) => {
  const response = await api.post<MessageResponse & { subscriber: NewsletterSubscriber }>(
    '/newsletter/subscribers/',
    data,
  );
  return response.data;
};

export const updateSubscriber = async (
  id: number,
  data: Partial<Pick<NewsletterSubscriber, 'first_name' | 'last_name' | 'name' | 'tags' | 'is_active'>>,
) => {
  const response = await api.patch<NewsletterSubscriber>(`/newsletter/subscribers/${id}/`, data);
  return response.data;
};

export const deleteSubscriber = async (id: number) => {
  await api.delete(`/newsletter/subscribers/${id}/`);
};

export const resendSubscriberConfirmation = async (id: number) => {
  const response = await api.post<MessageResponse>(`/newsletter/subscribers/${id}/resend-confirmation/`);
  return response.data;
};

export const bulkResendConfirmation = async (scope: 'pending' | 'unconfirmed' | 'all') => {
  const response = await api.post<MessageResponse & { queued: number }>(
    '/newsletter/subscribers/bulk-resend-confirmation/',
    { scope },
  );
  return response.data;
};

// A `format` paramétert a DRF a válasz-renderer kiválasztására foglalja → `export_format`.
export const exportSubscribers = async (exportFormat: 'csv' | 'xlsx', params?: SubscriberFilterParams) => {
  const response = await api.get<Blob>('/newsletter/subscribers/export/', {
    params: { ...params, export_format: exportFormat },
    responseType: 'blob',
  });
  return response.data;
};

// --- Kampányok (admin) ----------------------------------------------------------------

export const getCampaigns = async (params?: CampaignFilterParams) => {
  const response = await api.get<Paginated<EmailCampaign>>('/newsletter/campaigns/', { params });
  return response.data;
};

export const getCampaign = async (id: number) => {
  const response = await api.get<EmailCampaign>(`/newsletter/campaigns/${id}/`);
  return response.data;
};

export const createCampaign = async (data: CampaignWriteRequest) => {
  const response = await api.post<EmailCampaign>('/newsletter/campaigns/', data);
  return response.data;
};

export const updateCampaign = async (id: number, data: Partial<CampaignWriteRequest>) => {
  const response = await api.patch<EmailCampaign>(`/newsletter/campaigns/${id}/`, data);
  return response.data;
};

export const deleteCampaign = async (id: number) => {
  await api.delete(`/newsletter/campaigns/${id}/`);
};

type CampaignActionResponse = MessageResponse & { campaign: EmailCampaign };

export const sendCampaign = async (id: number) => {
  const response = await api.post<CampaignActionResponse>(`/newsletter/campaigns/${id}/send/`);
  return response.data;
};

export const resumeCampaign = async (id: number) => {
  const response = await api.post<CampaignActionResponse>(`/newsletter/campaigns/${id}/resume/`);
  return response.data;
};

export const scheduleCampaign = async (id: number, scheduledAt: string) => {
  const response = await api.post<CampaignActionResponse>(`/newsletter/campaigns/${id}/schedule/`, {
    scheduled_at: scheduledAt,
  });
  return response.data;
};

export const cancelCampaignSchedule = async (id: number) => {
  const response = await api.post<CampaignActionResponse>(`/newsletter/campaigns/${id}/cancel-schedule/`);
  return response.data;
};

export const archiveCampaign = async (id: number, archived: boolean) => {
  const response = await api.post<CampaignActionResponse>(`/newsletter/campaigns/${id}/archive/`, { archived });
  return response.data;
};

export const duplicateCampaign = async (id: number) => {
  const response = await api.post<CampaignActionResponse>(`/newsletter/campaigns/${id}/duplicate/`);
  return response.data;
};

export const getCampaignPreview = async (id: number) => {
  const response = await api.get<{ html: string; plain: string; subject: string; from: string }>(
    `/newsletter/campaigns/${id}/preview/`,
  );
  return response.data;
};

export const sendTestEmail = async (id: number, email: string) => {
  const response = await api.post<MessageResponse>(`/newsletter/campaigns/${id}/send-test/`, { email });
  return response.data;
};

export const getRecipientCount = async (recipientFilter: RecipientFilter, recipientTags: string[]) => {
  const response = await api.post<{ count: number }>('/newsletter/campaigns/recipient-count/', {
    recipient_filter: recipientFilter,
    recipient_tags: recipientTags,
  });
  return response.data.count;
};

export const getCampaignVariables = async () => {
  const response = await api.get<CampaignVariable[]>('/newsletter/campaigns/variables/');
  return response.data;
};

// --- Sablonok (admin) -------------------------------------------------------------------

export const getTemplates = async (params?: TemplateFilterParams) => {
  const response = await api.get<Paginated<EmailTemplate>>('/newsletter/templates/', { params });
  return response.data;
};

export const getCampaignTemplates = async () => {
  const response = await api.get<EmailTemplate[]>('/newsletter/templates/campaign-templates/');
  return response.data;
};

export const getTemplate = async (id: number) => {
  const response = await api.get<EmailTemplate>(`/newsletter/templates/${id}/`);
  return response.data;
};

export const createTemplate = async (data: CreateTemplateRequest) => {
  const response = await api.post<EmailTemplate>('/newsletter/templates/', data);
  return response.data;
};

export const updateTemplate = async (id: number, data: Partial<CreateTemplateRequest>) => {
  const response = await api.patch<EmailTemplate>(`/newsletter/templates/${id}/`, data);
  return response.data;
};

export const deleteTemplate = async (id: number) => {
  await api.delete(`/newsletter/templates/${id}/`);
};

// A64: szerver-oldalon renderelt HTML-előnézet — a korlátozott Django-motorral (extends +
// base.html-keret + behelyettesített minta-változók), tehát pontosan azt mutatja,
// amit a vevő kap. NEM a nyers html_content (ami {% %}/{{ }} forrás volna).
export const getEmailTemplatePreview = async (id: number): Promise<string> => {
  const response = await api.get<string>(`/admin/email-templates/${id}/preview/`, { responseType: 'text' });
  return response.data;
};

export const duplicateTemplate = async (id: number, name?: string) => {
  const response = await api.post<{ message: string; template: EmailTemplate }>(
    `/newsletter/templates/${id}/duplicate/`,
    name ? { name } : {},
  );
  return response.data.template;
};

// --- Statisztika -------------------------------------------------------------------------

export const getEmailAnalytics = async (dateFrom?: string, dateTo?: string) => {
  const response = await api.get<EmailAnalytics>('/newsletter/analytics/', {
    params: { date_from: dateFrom || undefined, date_to: dateTo || undefined },
  });
  return response.data;
};

// --- Nyilvános cégadatok (SiteSettings) -----------------------------------------------

export interface PublicSiteSettings {
  company_name: string;
  company_email: string;
  company_phone: string;
  company_address: string;
  company_tax_number?: string;
  company_legal_name?: string;
  company_registration_number?: string;
  /** Zárt bolt mód: false → a látogató a „Hamarosan” oldalt látja. */
  shop_open?: boolean;
}

export const getPublicSiteSettings = async () => {
  const response = await api.get<PublicSiteSettings>('/site-settings/');
  return response.data;
};
